import MarketingSeo from './MarketingSeo';
import MarketingFonts from './MarketingFonts';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import { useEffect, useRef } from 'react';
import { useApiRequest } from '~/core/hooks/use-api';
import LandingPage from './approved/LandingPage';
import type { MarketingPageKey } from './approved/marketing-page-types';
import { MarketingRuntimeContext } from './approved/runtime';
import type { DemoRequest } from './approved/runtime';
import styles from './marketing.module.css';

// Preserve server-rendered page content while separating secondary-page delivery.
const MarketingPages = dynamic(() => import('./approved/MarketingPages'));

const MarketingSignup = dynamic(() => import('./MarketingSignup'), {
  ssr: false,
});

export default function MarketingSite({ page }: { page?: MarketingPageKey }) {
  const router = useRouter();
  const marketingRoot = useRef<HTMLDivElement>(null);
  const subscribe = useApiRequest<{ success: boolean }, { email: string }>();
  const requestDemo = useApiRequest<{ success: boolean }, DemoRequest>();
  useEffect(() => {
    const root = marketingRoot.current;
    if (!root) return;
    const documentStyle = document.documentElement.style;
    const previousBehavior = documentStyle.scrollBehavior;
    const previousPadding = documentStyle.scrollPaddingTop;
    const motion = matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => {
      const tokens = getComputedStyle(root);
      documentStyle.scrollBehavior = tokens.scrollBehavior;
      documentStyle.scrollPaddingTop = tokens.scrollPaddingTop;
    };
    update();
    motion.addEventListener('change', update);
    return () => {
      motion.removeEventListener('change', update);
      documentStyle.scrollBehavior = previousBehavior;
      documentStyle.scrollPaddingTop = previousPadding;
    };
  }, []);
  return (
    <>
      <MarketingSeo pathname={page ? `/${page}` : '/'} />
      <MarketingFonts />
      <div ref={marketingRoot} className={styles.marketingRoot}>
        <MarketingRuntimeContext.Provider
          value={{
            signup: <MarketingSignup />,
            checkout: async (seats) => {
              await router.push(`/studio-checkout?seats=${seats}`);
            },
            subscribe: async (email) => {
              await subscribe({
                path: '/api/marketing/newsletter',
                body: { email },
              });
            },
            requestDemo: async (request) => {
              await requestDemo({
                path: '/api/marketing/demo-requests',
                body: request,
              });
            },
          }}
        >
          {page ? <MarketingPages pathname={`/${page}`} /> : <LandingPage />}
        </MarketingRuntimeContext.Provider>
      </div>
    </>
  );
}
